import { supabase } from "./client";
import { getReportableTimeLogIntervals, getLastScreenshotByTimeLogId, getSmartEndMs } from "@/lib/time-utils";
import { calculatePayableTimeSummary } from "@/lib/payable-time";
import { fetchSessionScreenshotEvidence } from "@/lib/screenshot-evidence";
import { fetchPaginated } from "@/lib/supabase-utils";
import type { LiveTrackingState } from "@/lib/live-tracking-status";
import { resolveScreenshotStorageObject } from "@/lib/screenshot-storage";

// Core entity types used by live tracking
export type Employee = { id: string; full_name: string; email?: string | null; avatar_url?: string | null };

export type Screenshot = {
  id: string;
  employee_id: string;
  taken_at: string; // normalized
  storage_path: string;
  activity_score?: number | null;
  duplicate_flag?: boolean | null;
  risk_score?: number | null;
  keystrokes?: number | null;
  mouse_clicks?: number | null;
  mouse_movements?: number | null;
};

export type AppLog = {
  id: string;
  employee_id: string;
  ts: string;
  app_name: string;
  window_title?: string | null;
};

export type UrlLog = {
  id: string;
  employee_id: string;
  ts: string;
  url: string;
  domain: string;
  title?: string | null;
};

export type ActivityLog = {
  id: string;
  employee_id: string;
  ts: string;
  keystrokes?: number | null;
  mouse_moves?: number | null;
  is_idle: boolean;
};

type Snapshot = {
  screenshots: Screenshot[];
  apps: AppLog[];
  urls: UrlLog[];
  activity: ActivityLog[];
  workedSeconds: number;
  idleSeconds: number;
  currentApp?: AppLog;
  currentUrl?: UrlLog;
  isIdle: boolean;
  trackingState: LiveTrackingState;
};

async function fetchCompleteLiveRows(query: any): Promise<any[]> {
  const maxRows = 5000;
  const rows = await fetchPaginated<any>({
    range: async (start: number, end: number) => {
      const result = await query.range(start, end);
      if (result.error) throw result.error;
      return result;
    },
  }, 500, maxRows);
  if (rows.length >= maxRows) throw new Error('Incomplete live evidence at row safety cap');
  return rows;
}

/**
 * Utility: normalize DB rows that have different column names across migrations
 * Priority order: taken_at > captured_at > started_at > timestamp > created_at
 * This ensures backward compatibility with older data while preferring standardized columns
 */
function coalesceTimestamp(row: any): string {
  return (
    row.taken_at ||
    row.captured_at ||
    row.started_at ||
    row.timestamp ||
    row.created_at ||
    new Date().toISOString()
  );
}

function toScreenshot(row: any): Screenshot {
  return {
    id: row.id,
    employee_id: row.user_id || row.employee_id,
    taken_at: row.captured_at || coalesceTimestamp(row),
    storage_path: row.image_url || row.file_path || row.storage_path || row.file_name || "",
    activity_score: row.activity_percent ?? row.total_activity_score ?? row.activity_score ?? null,
    duplicate_flag: row.duplicate_flag ?? row.is_duplicate ?? null,
    risk_score: row.risk_score ?? null,
    keystrokes: row.keystrokes ?? null,
    mouse_clicks: row.mouse_clicks ?? row.clicks ?? null,
    mouse_movements: row.mouse_movements ?? row.moves ?? null,
  };
}

function toAppLog(row: any): AppLog {
  return {
    id: row.id,
    employee_id: row.user_id || row.employee_id,
    ts: coalesceTimestamp(row),
    app_name: row.app_name || row.app || "",
    window_title: row.window_title ?? null,
  };
}

function toUrlLog(row: any): UrlLog {
  const url = row.site_url || row.url || "";
  const domain = row.domain || (typeof url === "string" ? safeExtractDomain(url) : "");
  return {
    id: row.id,
    employee_id: row.user_id || row.employee_id,
    ts: coalesceTimestamp(row),
    url,
    domain,
    title: row.title ?? null,
  };
}

function toActivityLog(row: any): ActivityLog {
  return {
    id: row.id,
    employee_id: row.user_id || row.employee_id,
    ts: coalesceTimestamp(row),
    keystrokes: row.keystrokes ?? null,
    mouse_moves: row.mouse_moves ?? null,
    is_idle: !!(row.is_idle ?? row.new_status === "idle"),
  };
}

function safeExtractDomain(rawUrl: string): string {
  try {
    const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return rawUrl || "";
  }
}

export async function listEmployees(q?: string, organizationId?: string | null, isSuperAdmin?: boolean): Promise<Employee[]> {
  let query = supabase
    .from("users")
    .select("id, full_name, email, avatar_url, organization_id")
    .in("role", ["employee", "manager", "admin"]) // visible employees
    .order("full_name", { ascending: true });

  if (q && q.trim().length > 0) {
    const like = `%${q.trim()}%`;
    query = query.ilike("full_name", like);
  }

  // Filter by organization for non-super admins
  if (organizationId && !isSuperAdmin) {
    query = query.eq("organization_id", organizationId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((u) => ({ id: u.id, full_name: u.full_name || "", email: u.email || null, avatar_url: u.avatar_url }));
}

export async function fetchTodaySnapshot(employeeId: string, fromISO: string): Promise<Snapshot> {
  const nowMs = Date.now();
  const toISO = new Date(nowMs).toISOString();
  const signal = AbortSignal.timeout(15000);
  const boundedClient = {
    from: (table: string) => ({
      select: (columns: string) => (supabase as any).from(table).select(columns).abortSignal(signal),
    }),
  };

  // Perform resilient queries to handle column name variations across environments
  const screenshotsRes = await (async () => {
    // screenshots table uses captured_at, not timestamp
    const base = boundedClient.from("screenshots").select("*").eq("user_id", employeeId).limit(100);
    let q = base.gte("captured_at", fromISO).lte("captured_at", toISO).order("captured_at", { ascending: false });
    let r = await q;
    if (r.error) {
      // fallback: no server-time filter
      r = await base.order("created_at", { ascending: false });
    }
    return r;
  })();

  const appsRes = await (async () => {
    const base = boundedClient.from("app_logs").select("*").eq("user_id", employeeId).limit(20);
    let q = base.gte("started_at", fromISO).lte("started_at", toISO).order("started_at", { ascending: false });
    let r = await q;
          if (r.error) {
        // app_url_activity uses started_at, not timestamp
        r = await base.order("started_at", { ascending: false });
      }
    return r;
  })();

  const urlsRes = await (async () => {
    const base = boundedClient.from("app_url_activity").select("*").eq("user_id", employeeId).limit(20);
    // app_url_activity uses started_at for timestamp
    let q = base.gte("started_at", fromISO).lte("started_at", toISO).order("started_at", { ascending: false });
    let r = await q;
    if (r.error) {
      // Fallback to created_at if started_at column doesn't exist
      r = await base.order("created_at", { ascending: false });
    }
    return r;
  })();

  const activityRes = await boundedClient
    .from("tracking_status_logs")
    .select("*")
    .eq("user_id", employeeId)
    .gte("timestamp", fromISO)
    .lte("timestamp", toISO)
    .order("timestamp", { ascending: false })
    .limit(200);

  const timeLogs = await fetchCompleteLiveRows(
    boundedClient.from('time_logs')
      .select('id, user_id, start_time, end_time, status, is_idle, idle_seconds, deducted_seconds')
      .eq('user_id', employeeId).lte('start_time', toISO)
      .or(`end_time.gte.${fromISO},end_time.is.null`)
      .order('start_time', { ascending: true }).order('id')
  );
  const idleLogs = await fetchCompleteLiveRows(
    boundedClient.from('idle_logs').select('user_id, idle_start, idle_end')
      .eq('user_id', employeeId).lte('idle_start', toISO)
      .or(`idle_end.gte.${fromISO},idle_end.is.null`)
      .order('idle_start').order('id')
  );

  if (screenshotsRes.error) throw screenshotsRes.error;
  if (appsRes.error) throw appsRes.error;
  if (urlsRes.error) throw urlsRes.error;
  if (activityRes.error) throw activityRes.error;

  const screenshots = (screenshotsRes.data || []).map(toScreenshot).filter((s) => new Date(s.taken_at).getTime() >= new Date(fromISO).getTime());
  const apps = (appsRes.data || []).map(toAppLog);
  const urls = (urlsRes.data || []).map(toUrlLog);
  const activity = (activityRes.data || []).map(toActivityLog);

  const options = { rangeStartMs: new Date(fromISO).getTime(), rangeEndMs: nowMs, nowMs };
  const evidence = await fetchSessionScreenshotEvidence(boundedClient, timeLogs, options);
  const screenshotsBySession = getLastScreenshotByTimeLogId(timeLogs, evidence, options);
  const intervals = getReportableTimeLogIntervals(timeLogs, { ...options, screenshotsBySession });
  const totals = calculatePayableTimeSummary(intervals, idleLogs, { ...options, fallbackTimeLogs: timeLogs });
  const reportableIds = new Set(intervals.map(interval => interval.logId));
  const currentSession = [...timeLogs].reverse().find(row => !row.end_time &&
    (!row.status || row.status === 'active') && reportableIds.has(row.id));
  // A historic activity sample cannot turn a completed session into a live one.
  const currentActivity = currentSession && activity.find(row =>
    new Date(row.ts).getTime() >= new Date(currentSession.start_time).getTime());
  // Reuse the reports' existing screenshot-freshness boundary. An open row
  // does not establish device presence when its last evidence has gone stale.
  const currentStartMs = currentSession && new Date(currentSession.start_time).getTime();
  const hasCurrentEvidence = !!currentSession && getSmartEndMs(currentStartMs, nowMs,
    screenshotsBySession.get(currentSession.id) ?? currentStartMs) === nowMs;
  const isIdle = hasCurrentEvidence && (currentActivity ? currentActivity.is_idle : currentSession.is_idle === true);
  const trackingState: LiveTrackingState = !currentSession ? 'stopped' : !hasCurrentEvidence ? 'unknown' : isIdle ? 'idle' : 'tracking';
  return {
    screenshots, apps, urls, activity,
    workedSeconds: totals.trackedSeconds, idleSeconds: totals.idleSeconds,
    currentApp: hasCurrentEvidence ? apps.find(row => new Date(row.ts).getTime() >= currentStartMs) : undefined,
    currentUrl: hasCurrentEvidence ? urls.find(row => new Date(row.ts).getTime() >= currentStartMs) : undefined,
    isIdle, trackingState,
  };
}

export function subscribeLiveToday(
  employeeId: string,
  fromISO: string,
  handlers: {
    onScreenshot?(row: Screenshot): void;
    onApp?(row: AppLog): void;
    onUrl?(row: UrlLog): void;
    onActivity?(row: ActivityLog): void;
    onTimeLogChange?(): void;
  }
): () => void {
  const channel = supabase
    .channel(`live:${employeeId}`)
    // IMPORTANT: views do not stream via Supabase Realtime. Subscribe to the table.
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "screenshots", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const s = toScreenshot(payload.new);
        if (new Date(s.taken_at).getTime() >= new Date(fromISO).getTime()) {
          handlers.onScreenshot?.(s);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "app_logs", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const a = toAppLog(payload.new);
        if (new Date(a.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onApp?.(a);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "app_logs", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const a = toAppLog(payload.new);
        if (new Date(a.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onApp?.(a);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "app_url_activity", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const u = toUrlLog(payload.new);
        if (new Date(u.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onUrl?.(u);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "app_url_activity", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const u = toUrlLog(payload.new);
        if (new Date(u.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onUrl?.(u);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "tracking_status_logs", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const al = toActivityLog(payload.new);
        if (new Date(al.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onActivity?.(al);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "tracking_status_logs", filter: `user_id=eq.${employeeId}` },
      (payload) => {
        const al = toActivityLog(payload.new);
        if (new Date(al.ts).getTime() >= new Date(fromISO).getTime()) {
          handlers.onActivity?.(al);
        }
      }
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "time_logs", filter: `user_id=eq.${employeeId}` },
      () => handlers.onTimeLogChange?.()
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "time_logs", filter: `user_id=eq.${employeeId}` },
      () => handlers.onTimeLogChange?.()
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "idle_logs", filter: `user_id=eq.${employeeId}` },
      () => handlers.onTimeLogChange?.()
    )
    .subscribe();

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      // ignore
    }
  };
}

// Simple in-memory memo for signed URLs
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

export async function getSignedScreenshotURLs(
  storagePaths: string[],
  ttlSec = 600,
): Promise<Map<string, string>> {
  const resolvedUrls = new Map<string, string>();
  const pendingByBucket = new Map<string, Array<{ storagePath: string; path: string }>>();
  const now = Date.now();

  for (const storagePath of [...new Set(storagePaths.filter(Boolean))]) {
    const isAbsoluteUrl = storagePath.startsWith("http://") || storagePath.startsWith("https://");
    const isStoredPublicScreenshotUrl = isAbsoluteUrl && (() => {
      try {
        return new URL(storagePath).pathname.startsWith(
          "/storage/v1/object/public/screenshots/",
        );
      } catch {
        return false;
      }
    })();

    if (isAbsoluteUrl && !isStoredPublicScreenshotUrl) {
      resolvedUrls.set(storagePath, storagePath);
      continue;
    }

    const cached = signedUrlCache.get(storagePath);
    if (cached && cached.expiresAt > now + 5_000) {
      resolvedUrls.set(storagePath, cached.url);
      continue;
    }

    const storageObject = resolveScreenshotStorageObject(storagePath);
    const pending = pendingByBucket.get(storageObject.bucket) || [];
    pending.push({ storagePath, path: storageObject.path });
    pendingByBucket.set(storageObject.bucket, pending);
  }

  await Promise.all(
    [...pendingByBucket.entries()].map(async ([bucket, pending]) => {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrls(pending.map(({ path }) => path), ttlSec);

      if (error) throw error;

      pending.forEach(({ storagePath }, index) => {
        const signedUrl = data?.[index]?.signedUrl;
        if (!signedUrl) return;

        resolvedUrls.set(storagePath, signedUrl);
        signedUrlCache.set(storagePath, {
          url: signedUrl,
          expiresAt: now + ttlSec * 1000,
        });
      });
    }),
  );

  return resolvedUrls;
}

export async function getSignedScreenshotURL(storagePath: string, ttlSec = 600): Promise<string> {
  if (!storagePath) return "";

  const signedUrls = await getSignedScreenshotURLs([storagePath], ttlSec);
  return signedUrls.get(storagePath) || "";
}
